import * as React from 'react';

import { FaOptionIcon, NamedIconsType } from '@europrocurement/l2d-icons';
import { Button, ButtonProps, PaletteColor, Theme, Typography, useTheme } from '@mui/material';
import { TextIcon } from '../TextIcon';

export type FlexyButtonProps = {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    label?: string;
    color?: PaletteColor | string;
    icon?: NamedIconsType;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    variant?: 'contained' | 'outlined' | 'text';
    style?: React.CSSProperties;
    type?: ButtonProps['type'];
};

// Utilitaire pour valider et normaliser une couleur hex
const isValidHexColor = (hex: unknown): hex is string => {
    if (typeof hex !== 'string') {
        return false;
    }
    const hexPattern = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexPattern.test(hex);
};

const normalizeHexColor = (hex: string): string => {
    let normalizedHex = hex.trim();

    if (!normalizedHex.startsWith('#')) {
        normalizedHex = `#${normalizedHex}`;
    }

    // Convertir les couleurs courtes (ex: #FFF) en format long (ex: #FFFFFF)
    if (normalizedHex.length === 4) {
        normalizedHex = `#${normalizedHex[1]}${normalizedHex[1]}${normalizedHex[2]}${normalizedHex[2]}${normalizedHex[3]}${normalizedHex[3]}`;
    }

    return normalizedHex;
};

// Utilitaire pour créer un objet similaire à PaletteColor à partir d'une couleur hex
const createPaletteFromHex = (hexColor: string) => {
    // Validation et normalisation de la couleur hex
    if (!isValidHexColor(hexColor)) {
        console.warn(`Couleur hex invalide: ${hexColor}, utilisation de la couleur par défaut`);
        return {
            main: '#000000',
            dark: '#000000',
            light: '#333333',
            contrastText: '#ffffff',
        };
    }

    const normalizedHex = normalizeHexColor(hexColor);

    // Fonction pour convertir hex en RGB
    const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
        const cleanHex = hex.replace('#', '');

        if (cleanHex.length !== 6) {
            console.warn(`Format hex invalide après normalisation: ${hex}`);
            return { r: 0, g: 0, b: 0 };
        }

        const bigint = parseInt(cleanHex, 16);

        if (Number.isNaN(bigint)) {
            console.warn(`Impossible de parser la couleur hex: ${hex}`);
            return { r: 0, g: 0, b: 0 };
        }

        /* eslint-disable no-bitwise */
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        /* eslint-enable no-bitwise */

        return { r, g, b };
    };

    // Fonction pour convertir RGB en hex
    const rgbToHex = (r: number, g: number, b: number): string => {
        const clamp = (value: number): number => Math.min(255, Math.max(0, Math.round(value)));
        const toHex = (value: number): string => clamp(value).toString(16).padStart(2, '0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    // Fonction pour assombrir une couleur (pour .dark)
    const darkenColor = (hex: string, percent: number = 20): string => {
        const rgb = hexToRgb(hex);
        const factor = 1 - percent / 100;

        return rgbToHex(rgb.r * factor, rgb.g * factor, rgb.b * factor);
    };

    // Fonction pour éclaircir une couleur (pour .light)
    const lightenColor = (hex: string, percent: number = 20): string => {
        const rgb = hexToRgb(hex);
        const factor = percent / 100;

        return rgbToHex(
            rgb.r + (255 - rgb.r) * factor,
            rgb.g + (255 - rgb.g) * factor,
            rgb.b + (255 - rgb.b) * factor,
        );
    };

    try {
        return {
            main: normalizedHex,
            dark: darkenColor(normalizedHex, 20),
            light: lightenColor(normalizedHex, 20),
            contrastText: '#ffffff',
        };
    } catch (error) {
        console.error(`Erreur lors de la création de la palette à partir de ${hexColor}:`, error);
        return {
            main: '#000000',
            dark: '#000000',
            light: '#333333',
            contrastText: '#ffffff',
        };
    }
};

// Type guard pour vérifier si c'est un PaletteColor
const isPaletteColor = (color: PaletteColor | string): color is PaletteColor =>
    typeof color === 'object' && color !== null && 'main' in color;

const createDisabledColors = (theme: Theme) => ({
    main: theme.palette.action.disabled,
    dark: theme.palette.action.disabled,
    light: theme.palette.action.disabled,
    contrastText: theme.palette.text.disabled,
});

export const FlexyButton: React.FunctionComponent<FlexyButtonProps> = function ({
    onClick,
    label,
    color,
    icon,
    disabled = false,
    size = 'small',
    variant = 'contained',
    type = 'button',
    style,
}: FlexyButtonProps) {
    const theme = useTheme();

    const disabledColors = React.useMemo(() => createDisabledColors(theme), [theme]);

    const mainColor = React.useMemo(() => {
        if (disabled) {
            return disabledColors;
        }

        if (!color) {
            return theme.palette.primary;
        }

        if (isPaletteColor(color)) {
            return color;
        }

        // Si c'est une string (couleur hex), on crée un objet similaire à PaletteColor
        if (typeof color === 'string') {
            return createPaletteFromHex(color);
        }

        // Fallback au cas où
        console.warn('Type de couleur non reconnu:', typeof color, color);
        return theme.palette.primary;
    }, [color, theme.palette.primary, disabled, disabledColors]);

    const textColor = React.useMemo(() => {
        if (disabled) {
            return theme.palette.text.disabled;
        }
        return theme.palette.background.paper;
    }, [disabled, theme.palette.text.disabled, theme.palette.background.paper]);

    // Tailles de bouton basées sur la propriété size
    const buttonSizes = {
        small: { button: 32, icon: 16, borderRadius: 6 },
        medium: { button: 40, icon: 20, borderRadius: 8 },
        large: { button: 48, icon: 24, borderRadius: 10 },
    };

    const sizeConfig = buttonSizes[size];

    const getVariantStyles = React.useCallback(
        (variantType: string): React.CSSProperties => {
            const baseStyle: React.CSSProperties = {
                color: textColor,
                backgroundColor: mainColor.dark,
                verticalAlign: 'middle',
            };

            if (disabled) {
                const disabledBaseStyle: React.CSSProperties = {
                    ...baseStyle,
                    color: theme.palette.text.disabled,
                    backgroundColor: theme.palette.action.disabledBackground,
                    cursor: 'not-allowed',
                    opacity: 0.6, // Ajouter une opacité
                };

                if (variantType === 'outlined') {
                    return {
                        ...disabledBaseStyle,
                        backgroundColor: 'transparent',
                        border: `1px solid ${theme.palette.action.disabled}`,
                    };
                }

                if (variantType === 'text') {
                    return {
                        ...disabledBaseStyle,
                        backgroundColor: 'transparent',
                    };
                }

                return disabledBaseStyle;
            }

            if (variantType === 'outlined') {
                return {
                    ...baseStyle,
                    backgroundColor: 'transparent',
                    color: mainColor.main,
                    border: `1px solid ${mainColor.main}`,
                };
            }

            if (variantType === 'text') {
                return {
                    ...baseStyle,
                    backgroundColor: 'transparent',
                    color: mainColor.main,
                };
            }

            return baseStyle;
        },
        [disabled, mainColor.dark, mainColor.main, textColor, theme],
    );

    const globalStyle: React.CSSProperties = React.useMemo(
        () => getVariantStyles(variant),
        [getVariantStyles, variant],
    );

    const specificStyle = React.useMemo<React.CSSProperties>(() => {
        const hasLabel = Boolean(label);
        const hasIcon = Boolean(icon);

        if (hasLabel && !hasIcon) {
            return {
                padding: '8px 12px',
            };
        }

        if (!hasLabel && hasIcon) {
            return {
                minWidth: 'unset',
                width: `${sizeConfig.button}px`,
                height: `${sizeConfig.button}px`,
                padding: 0,
                margin: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                borderRadius: `${sizeConfig.borderRadius}px`,
                lineHeight: 1,
                boxSizing: 'border-box',
            };
        }

        if (hasLabel && hasIcon) {
            return {
                padding: '8px 12px',
            };
        }

        return {};
    }, [icon, label, sizeConfig.borderRadius, sizeConfig.button]);

    const getIconColors = React.useCallback(() => {
        if (disabled) {
            return {
                iconColor: theme.palette.action.disabled,
                iconOptionColor: theme.palette.action.disabled,
            };
        }

        const defaultColor = theme.palette.primary;

        if (!color) {
            return {
                iconColor: defaultColor.main,
                iconOptionColor: defaultColor.dark,
            };
        }

        if (isPaletteColor(color)) {
            return {
                iconColor: color.main,
                iconOptionColor: color.dark,
            };
        }

        if (typeof color === 'string') {
            return {
                iconColor: color,
                iconOptionColor: mainColor.dark,
            };
        }

        return {
            iconColor: defaultColor.main,
            iconOptionColor: defaultColor.dark,
        };
    }, [color, disabled, mainColor.dark, theme]);

    const buttonContent = React.useMemo<React.ReactNode>(() => {
        const hasLabel = Boolean(label);
        const hasIcon = Boolean(icon);

        if (hasLabel && !hasIcon) {
            return (
                <Typography
                    sx={{
                        color: disabled ? theme.palette.text.disabled : 'inherit',
                    }}
                >
                    {label}
                </Typography>
            );
        }

        if (!hasLabel && hasIcon && icon) {
            const { iconColor, iconOptionColor } = getIconColors();

            return (
                <FaOptionIcon
                    {...icon.props}
                    size={undefined}
                    color={iconColor}
                    optionColor={iconOptionColor}
                    disabled={disabled}
                    optionProps={{
                        ...icon.props.optionProps,
                        style: disabled
                            ? {
                                  ...icon.props.optionProps?.style,
                                  opacity: 0.6,
                              }
                            : icon.props.optionProps?.style,
                    }}
                    iconProps={{
                        radius: 100,
                        ...icon.props.iconProps,
                        width: sizeConfig.icon,
                        height: sizeConfig.icon,
                    }}
                    sx={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        margin: 0,
                        opacity: disabled ? 0.6 : 1,
                    }}
                />
            );
        }

        if (hasLabel && hasIcon && icon) {
            return (
                <TextIcon
                    gap="8px"
                    icon={icon}
                    {...(disabled && { disabled: true })}
                    sx={{
                        opacity: disabled ? 0.6 : 1,
                        color: disabled ? theme.palette.text.disabled : 'inherit',
                    }}
                >
                    {label}
                </TextIcon>
            );
        }

        return null;
    }, [label, icon, disabled, theme.palette.text.disabled, getIconColors, sizeConfig.icon]);

    const backgroundColor = React.useMemo(() => {
        if (variant === 'contained') {
            return mainColor.main;
        }

        if (variant === 'outlined') {
            return `${mainColor.main}08`;
        }

        return `${mainColor.main}08`;
    }, [mainColor.main, variant]);

    return (
        <Button
            type={type}
            href=""
            size="small"
            variant={variant}
            onClick={onClick || undefined}
            disabled={disabled}
            sx={{
                ...globalStyle,
                ...specificStyle,
                ...(!disabled && {
                    '&:hover': {
                        backgroundColor,
                        ...(variant === 'outlined' && {
                            borderColor: mainColor.dark,
                        }),
                    },
                }),
            }}
            style={{
                ...style,
            }}
        >
            {buttonContent}
        </Button>
    );
};
